import { Form, FormRow, PopupHint, Select } from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import cities from '../../constants/cities';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const ForestQuestions = (insurance) => {
  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });
  const { state, validateInsurance } = useInsuranceOffer();
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );
  const forestDetails = state.selectedInsurances[index].questions;

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-forestMunicipality`}
          label={<Message id="labelText.forestMunicipality" />}
          labelFor={buildElementId(insurance.id, 'FOREST_MUNICIPALITY')}
          required
        >
          <Select
            value={getValue(forestDetails.forestType)}
            aria-describedby='forestMunicipalityError'
            name={`${insurance.id}-forestMunicipality`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  forestType: makeInputData(
                    e.target.value,
                    'forestMunicipality',                 
                    (
                      cities[e.target.selectedIndex - 1] || {
                        text: 'uiText.choose',
                      }
                    ).text,
                    'error.fill.in.missing.information'                 
                  ),
                },
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'FOREST_MUNICIPALITY')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>

            {cities.map((item) => {
              return (
                <option key={item.value} value={item.value}>
                  {getMessage(item.text)}
                </option>
              );
            })}
          </Select>
          <ValidationError
            id='forestMunicipalityError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'FOREST_MUNICIPALITY')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
      <div>
        <FormInput
            type="propertycode"
            value={getValue(forestDetails.propertyCode)}
            inputId={buildElementId(insurance.id, 'FOREST_PROPERTYCODE')}
            onChange={(propertyCode , error) => {
              forestDetails.propertyCode = makeInputData(
                propertyCode,
                'forestPropertCode'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: forestDetails,
                questionId: `${insurance.id}-forestPropertCode`,
                error,
              });                
            }}
            label={getMessage('labelText.forestPropertCode')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.forestPropertCode" />
              </PopupHint>
            }
            required
        />
        <FormInput
            type="sport"
            maxLength={100}
            value={getValue(forestDetails.nameOfTheForestEstateAndLocality)}
            inputId={buildElementId(insurance.id, 'FOREST_NAME_OF_THE_FOREST_ESTATE_AND_LOCALITY')}
            onChange={(nameOfTheForestEstateAndLocality , error) => {
              forestDetails.nameOfTheForestEstateAndLocality = makeInputData(
                nameOfTheForestEstateAndLocality,
                'forestNameOfTheForestEstateAndLocality'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: forestDetails,
                questionId: `${insurance.id}-forestNameOfTheForestEstateAndLocality`,
                error,
              });                
            }}
            label={getMessage('labelText.forestNameOfTheForestEstateAndLocality')}
            required
        />      
        <FormInput
            type="numeric"
            limit={99999}
            value={getValue(forestDetails.area)}
            inputId={buildElementId(insurance.id, 'FOREST_AREA')}
            onChange={(area , error) => {
              forestDetails.area = makeInputData(
                area,
                'forestArea'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: forestDetails,
                questionId: `${insurance.id}-forestArea`,
                error,
              });                
            }}
            label={getMessage('labelText.forestArea')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.forestArea" />
              </PopupHint>
            }
            required
        />  
        
        <FormRow
          dataKey={`${insurance.id}-forestIndemnity`}
          label={<Message id="labelText.forestIndemnity" />}
          labelFor={buildElementId(insurance.id, 'FOREST_INDEMNITY')}
          hint={
            <PopupHint placement="top">
              <Message id="hintText.forestIndemnity" />
            </PopupHint>
          }
          required
        >
          <Select
            value={getValue(forestDetails.indemnity)}
            aria-describedby='forestIndemnityError'
            name={`${insurance.id}-indemnity`}
            onChange={(e) => {
              forestDetails.indemnity = makeInputData(
                e.target.value,
                'forestIndemnity',
                getOptionTitle(e, 'forestIndemnity'),
                'error.fill.in.missing.information'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: forestDetails,
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'FOREST_INDEMNITY')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="1" key="1">
              {getMessage('option.forestIndemnity.001')}
            </option>
            <option value="2" key="2">
              {getMessage('option.forestIndemnity.002')}
            </option>
            <option value="3" key="3">
              {getMessage('option.forestIndemnity.003')}
            </option>
            <option value="4" key="4">
              {getMessage('option.forestIndemnity.004')}
            </option>
            <option value="5" key="5">
              {getMessage('option.forestIndemnity.005')}
            </option>
          </Select>
          <ValidationError
            id='forestIndemnityError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'FOREST_INDEMNITY')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
        <FormRow
          dataKey={`${insurance.id}-forestGrowthStage`}
          label={<Message id="labelText.forestGrowthStage" />}
          labelFor={buildElementId(insurance.id, 'FOREST_GROWTHSTAGE')}
          hint={
            <PopupHint placement="top">
              <Message id="hintText.forestGrowthStage" />
            </PopupHint>
          }
          required
        >
          <Select
            value={getValue(forestDetails.growthstage)}
            aria-describedby='forestGrowthStageError'
            name={`${insurance.id}-growthstage`}
            onChange={(e) => {
              forestDetails.growthstage = makeInputData(
                e.target.value,
                'forestGrowthStage',
                getOptionTitle(e, 'forestGrowthStage'),
                'error.fill.in.missing.information'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: forestDetails,
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'FOREST_GROWTHSTAGE')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="1" key="1">
              {getMessage('option.forestGrowthStage.001')}
            </option>
            <option value="2" key="2">
              {getMessage('option.forestGrowthStage.002')}
            </option>
            <option value="3" key="3">
              {getMessage('option.forestGrowthStage.003')}
            </option>
          </Select>
          <ValidationError
            id='forestGrowthStageError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'FOREST_GROWTHSTAGE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
    </Form>
  );
};

export default ForestQuestions;
